import { Flex, HelpIndicator, InfoBox, Text } from "@jsq/ds/core";
import {
  FormCheckbox,
  FormDatePicker,
  FormRadioButton,
  FormRadioGroup,
  useFormContext,
} from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { useFeatureFlag } from "@jsq/flags-react";
import { formatDate } from "~/pages/FundAccounting/utils/helpers";
import { SPACING } from "~/styles/variables";
import { SYNC_CUSTOM_DATE_SETTINGS_TYPES, SYNC_SETTINGS_TYPES } from "./constants";
import * as styles from "./styles_vanilla.css";
import { InitialValues } from "./types";

type Props = {
  batchDates: string[];
  glStartDate?: string;
  glAsOfDate?: string;
  syncFromTransferHistory: boolean;
};

const CustomDateCard = ({
  batchDates = [],
  glStartDate = "",
  glAsOfDate = "",
  syncFromTransferHistory,
}: Props) => {
  const enableResyncTransfer = useFeatureFlag("resyncTransfer", false);
  const { values } = useFormContext<InitialValues>();

  const noUnsyncedBatches = batchDates.length === 0;

  const batchDatesContent = () => {
    const firstThreeDates = batchDates.slice(0, 3);
    const tooltip = firstThreeDates.map((date) => (
      <Text color="white" key={date} variant="secondary-sm">
        {formatDate(date)}
      </Text>
    ));
    if (batchDates.length > 5) {
      tooltip.push(
        <Text color="white" key="more" variant="secondary-sm">
          ...
        </Text>,
      );
    }
    return tooltip;
  };

  return (
    <>
      <FormRadioGroup name="syncSettings">
        <Text variant="strong-md">What are you syncing?</Text>

        <FormRadioButton
          disabled={noUnsyncedBatches || (enableResyncTransfer && syncFromTransferHistory)}
          value={SYNC_SETTINGS_TYPES.allTransferBatches}
        >
          <Flex alignItems="center">
            <Text>All unsynced transfer events ({batchDates.length})</Text>
            {!noUnsyncedBatches && batchDates.length > 0 && (
              <HelpIndicator id="transfer-sync-all" content={batchDatesContent()}>
                <Icon style={{ marginLeft: "8px" }} type="question-circle" variant="outline" />
              </HelpIndicator>
            )}
          </Flex>
        </FormRadioButton>

        <FormRadioButton value={SYNC_SETTINGS_TYPES.untilCustomDate}>
          <Flex flexDirection="column">
            Transfer events up until a custom date
            {[SYNC_SETTINGS_TYPES.untilCustomDate].includes(values.syncSettings) && (
              <div className={styles.subText}>
                <Text variant={"secondary-sm"}>Select a date</Text>
              </div>
            )}
          </Flex>
        </FormRadioButton>
      </FormRadioGroup>
      <FormRadioGroup name="syncCustomDateSettings">
        {[SYNC_SETTINGS_TYPES.untilCustomDate].includes(values.syncSettings) &&
          (enableResyncTransfer ? (
            <>
              <Flex
                css={{ marginTop: SPACING.MEDIUM }}
                flexDirection="column"
                alignItems="flex-start"
              >
                <Text variant="strong-md">Integration start</Text>
                <FormDatePicker
                  displayErrorMessage
                  name="glStartDate"
                  hasIcon
                  disabled={values.useInceptionDate}
                  placeholderText={values.useInceptionDate ? glStartDate : ""}
                />
                <Flex css={{ marginTop: SPACING.SMALL }} flexDirection="column">
                  <FormCheckbox
                    name="useInceptionDate"
                    label="Use inception date"
                    disabled={false}
                  />
                </Flex>
              </Flex>
              <Flex css={{ marginTop: SPACING.MEDIUM }} flexDirection="column">
                <Text variant="strong-md">Integration through</Text>
                <FormDatePicker displayErrorMessage name="customGlDate" hasIcon />
              </Flex>
              <>
                <br></br>
                <InfoBox
                  variant="info"
                  title="Processing time may take a long time depending on your date range"
                >
                  Larger date ranges and more transactions will take longer to preview and sync.
                  To set expectations, selecting a quarter with 500 batches of GL transactions
                  will take about 10 seconds to process.
                </InfoBox>
              </>
            </>
          ) : (
            <Flex className={styles.CustomDateSection} flexDirection="column">
              <Text className={styles.RadioGroupItem} variant="strong-md">
                Through what date?
              </Text>
              <div className={styles.RadioGroupItem}>
                <FormRadioButton value={SYNC_CUSTOM_DATE_SETTINGS_TYPES.untilLastSyncedGlDate}>
                  <Flex flexDirection="column">
                    <div className={styles.titleWithSubText}>Until last synced GL date</div>
                    {glAsOfDate && (
                      <div>
                        <Text variant={"secondary-sm"}>Until {glAsOfDate}</Text>
                      </div>
                    )}
                  </Flex>
                </FormRadioButton>
              </div>

              <FormRadioButton value={SYNC_CUSTOM_DATE_SETTINGS_TYPES.customGlDate}>
                Custom GL date
              </FormRadioButton>

              {values.syncCustomDateSettings ===
                SYNC_CUSTOM_DATE_SETTINGS_TYPES.customGlDate && (
                <Flex className={styles.CustomDataPicker} flexDirection="column">
                  <Text variant="strong-md">Custom GL date</Text>
                  <FormDatePicker name={SYNC_CUSTOM_DATE_SETTINGS_TYPES.customGlDate} hasIcon />
                </Flex>
              )}
            </Flex>
          ))}
      </FormRadioGroup>
    </>
  );
};

export default CustomDateCard;
